import React from 'react';
import { Icon } from '@iconify/react';
import type { CashflowSummary } from '@/types/cashflow';
import { formatRupiahPlain, formatRupiahSigned, SHORT_MONTH_NAMES_ID } from '@/utils/cashflowUtils';

interface CashflowSummaryCardsProps {
  summary: CashflowSummary | null;
  selectedMonth: number;
  selectedYear: number;
  isLoading?: boolean;
  onOpenAccounts?: () => void;
}

export const CashflowSummaryCards: React.FC<CashflowSummaryCardsProps> = ({
  summary,
  selectedMonth,
  selectedYear,
  isLoading,
  onOpenAccounts,
}) => {
  const shortMonth = SHORT_MONTH_NAMES_ID[selectedMonth - 1] || 'Sep';
  const monthYearLabel = `${shortMonth} ${selectedYear}`;

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3.5 lg:gap-5 !mt-0 animate-pulse">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="bg-white rounded-2xl p-3.5 sm:p-4 border border-[#E0E6ED] shadow-2xs flex flex-col justify-between min-h-[110px]"
          >
            <div className="flex items-start justify-between gap-1">
              <div className="h-3.5 bg-gray-200 rounded w-20" />
              <div className="w-4 h-4 rounded-full bg-gray-200 shrink-0" />
            </div>
            <div className="my-2">
              <div className="h-5 bg-gray-200 rounded w-28" />
            </div>
            <div className="h-2.5 bg-gray-100 rounded w-24" />
          </div>
        ))}
      </div>
    );
  }

  const income = summary?.total_income ?? 0;
  const pending = summary?.total_pending ?? 0;
  const expense = summary?.total_expense ?? 0;
  const net = summary?.net_balance ?? 0;
  const availableCash = summary?.available_cash ?? 0;

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3.5 lg:gap-5 !mt-0">
      {/* 1. Card Top-Left: Masuk [Bulan Tahun] */}
      <div className="bg-white rounded-2xl p-4 sm:p-5 border border-[#E0E6ED] shadow-2xs flex flex-col justify-between min-h-[118px]">
        <div className="flex items-start justify-between gap-1">
          <span className="text-xs font-semibold text-gray-500 leading-tight">
            Masuk {monthYearLabel}
          </span>
          <div className="text-[#FC480D] shrink-0">
            <Icon icon="ph:arrow-circle-down" className="w-5 h-5" />
          </div>
        </div>
        <div className="my-1.5">
          <div className="text-xl sm:text-2xl font-bold tracking-tight text-[#059669] truncate">
            {formatRupiahPlain(income)}
          </div>
        </div>
        <p className="text-xs text-gray-400 truncate">
          Event PAID · input manual
        </p>
      </div>

      {/* 2. Card Top-Right: Menunggu pembayaran */}
      <div className="bg-white rounded-2xl p-4 sm:p-5 border border-[#E0E6ED] shadow-2xs flex flex-col justify-between min-h-[118px]">
        <div className="flex items-start justify-between gap-1">
          <span className="text-xs font-semibold text-gray-500 leading-tight">
            Menunggu pembayaran
          </span>
          <div className="text-[#FC480D] shrink-0">
            <Icon icon="ph:clock-clockwise" className="w-5 h-5" />
          </div>
        </div>
        <div className="my-1.5">
          <div className="text-xl sm:text-2xl font-bold tracking-tight text-[#D97706] truncate">
            {formatRupiahPlain(pending)}
          </div>
        </div>
        <p className="text-xs text-gray-400 truncate">
          Status PENDING/UNPAID
        </p>
      </div>

      {/* 3. Card Bottom-Left: Pengeluaran [Bulan Tahun] */}
      <div className="bg-white rounded-2xl p-4 sm:p-5 border border-[#E0E6ED] shadow-2xs flex flex-col justify-between min-h-[118px]">
        <div className="flex items-start justify-between gap-1">
          <span className="text-xs font-semibold text-gray-500 leading-tight">
            Pengeluaran {monthYearLabel}
          </span>
          <div className="text-[#FC480D] shrink-0">
            <Icon icon="ph:arrow-circle-up" className="w-5 h-5" />
          </div>
        </div>
        <div className="my-1.5">
          <div className="text-xl sm:text-2xl font-bold tracking-tight text-[#EF4444] truncate">
            {formatRupiahPlain(expense)}
          </div>
        </div>
        <p className="text-xs text-gray-400 truncate">
          Event · di luar event
        </p>
      </div>

      {/* 4. Card Bottom-Right: Saldo bersih bulan ini */}
      <div
        role={onOpenAccounts ? 'button' : undefined}
        tabIndex={onOpenAccounts ? 0 : undefined}
        onClick={onOpenAccounts}
        className={`bg-white rounded-2xl p-4 sm:p-5 border border-[#E0E6ED] shadow-2xs flex flex-col justify-between min-h-[118px] ${
          onOpenAccounts ? 'cursor-pointer hover:border-orange-200 transition-colors' : ''
        }`}
      >
        <div className="flex items-start justify-between gap-1">
          <span className="text-xs font-semibold text-gray-500 leading-tight">
            Saldo bersih bulan ini
          </span>
          <div className="text-[#FC480D] shrink-0">
            <Icon icon="ph:scales" className="w-5 h-5" />
          </div>
        </div>
        <div className="my-1.5">
          <div className="text-xl sm:text-2xl font-bold tracking-tight text-[#0E1726] truncate">
            {formatRupiahSigned(net)}
          </div>
        </div>
        <p className="text-xs text-gray-400 truncate">
          Kas tersedia {formatRupiahPlain(availableCash)}
        </p>
      </div>
    </div>
  );
};
